// ─────────────────────────────────────────────────────────────────
//  PatientShared.jsx  —  Shared components v2
// ─────────────────────────────────────────────────────────────────
import React from "react";
import { Typography, Tag } from "antd";
import {
  G700, G100, G50, GRAY7, GRAY6,
  SLATE, MUTED, WHITE, VISIT_STATUS, OPD_STATUS,
  RADIUS_MD, RADIUS_SM, RADIUS_PILL,
  SHADOW_SM, FF_DISPLAY, FF_BODY, FF_MONO,
  TRANSITION_MD,
} from "./patientTokens";

const { Text } = Typography;

/* ── Status badge ────────────────────────────────────────────── */
export function StatusBadge({ status }) {
  const s = VISIT_STATUS[status] || { color: MUTED, bg: GRAY7, label: status || "—" };
  return (
    <span className="pd-status" style={{ color: s.color, background: s.bg }}>
      <span style={{
        width: 5, height: 5, borderRadius: "50%",
        background: s.color, display: "inline-block", flexShrink: 0,
      }} />
      {s.label}
    </span>
  );
}

/* ── OPD Tag ─────────────────────────────────────────────────── */
export function OpdTag({ value }) {
  const s = OPD_STATUS[value] || { label: value || "—", color: "default" };
  return (
    <Tag
      color={s.color}
      style={{ borderRadius: RADIUS_XS_FALLBACK, fontSize: 11, fontWeight: 500, padding: "1px 9px" }}
    >
      {s.label}
    </Tag>
  );
}
const RADIUS_XS_FALLBACK = 7; // small local constant to avoid extra import churn

/* ── Stat card — Apple-style elevated tile with hover lift ─────── */
export function StatCard({ icon, label, value, sub, delay = "", accent = G700 }) {
  return (
    <div
      className={`pd-stat pd-up pd-card-hover ${delay}`}
      style={{
        background: WHITE, borderRadius: RADIUS_MD + 4,
        border: `1px solid ${GRAY6}`,
        boxShadow: SHADOW_SM,
        padding: "18px 16px",
      }}
    >
      <div style={{
        width: 40, height: 40, borderRadius: RADIUS_SM + 2, marginBottom: 12,
        background: `${accent}14`,
        display: "flex", alignItems: "center", justifyContent: "center",
        fontSize: 18, color: accent,
      }}>
        {icon}
      </div>
      <Text style={{
        color: SLATE, fontSize: 27, fontWeight: 800,
        display: "block", lineHeight: 1, letterSpacing: "-0.8px",
        fontFamily: FF_DISPLAY,
      }}>
        {value}
      </Text>
      <Text style={{
        color: MUTED, fontSize: 11.5, fontWeight: 500,
        display: "block", letterSpacing: "0.2px", marginTop: 4,
      }}>
        {label}
      </Text>
      {sub && (
        <Text style={{ color: accent, fontSize: 10.5, fontWeight: 600, display: "block", marginTop: 3 }}>
          {sub}
        </Text>
      )}
    </div>
  );
}

/* ── Visit table columns — responsive: hide secondary cols on small screens ── */
export const buildVisitColumns = (onDetails) => [
  {
    title: "Date",
    dataIndex: "visited_no",
    key: "visited_no",
    render: (v) => {
      const d = new Date(v);
      return (
        <div>
          <Text style={{ fontSize: 13, fontWeight: 600, color: SLATE, display: "block", letterSpacing: "-0.2px" }}>
            {d.toLocaleDateString("en-GB", { day: "numeric", month: "short", year: "numeric" })}
          </Text>
          <Text style={{ fontSize: 11, color: MUTED, fontFamily: FF_MONO }}>
            {d.toLocaleTimeString("en-GB", { hour: "2-digit", minute: "2-digit" })}
          </Text>
        </div>
      );
    },
  },
  {
    title: "PRN",
    dataIndex: "prn",
    key: "prn",
    responsive: ["sm"],
    render: (v) => (
      <Tag
        style={{
          borderRadius: RADIUS_XS_FALLBACK, fontWeight: 600, fontSize: 11,
          background: G50, borderColor: G100, color: G700,
          fontFamily: FF_MONO,
        }}
      >
        {v}
      </Tag>
    ),
  },
  {
    title: "Status",
    dataIndex: "status",
    key: "status",
    render: (v) => <StatusBadge status={v} />,
  },
  {
    title: "OPD",
    dataIndex: "opd_status",
    key: "opd_status",
    responsive: ["md"],
    render: (v) => <OpdTag value={v} />,
  },
  {
    title: "Medical",
    dataIndex: "medical_status",
    key: "medical_status",
    responsive: ["lg"],
    render: (v) => (
      <Text style={{ color: MUTED, fontSize: 12 }}>{v || "—"}</Text>
    ),
  },
  {
    title: "",
    key: "action",
    render: (_, record) => (
      <button
        onClick={() => onDetails(record)}
        style={{
          background: G50, border: `1px solid ${G100}`,
          borderRadius: RADIUS_SM, cursor: "pointer",
          color: G700, fontWeight: 600, fontSize: 12,
          padding: "7px 14px", fontFamily: FF_DISPLAY,
          transition: TRANSITION_MD,
          whiteSpace: "nowrap",
          minHeight: 36,
        }}
        onMouseEnter={e => { e.currentTarget.style.background = G100; }}
        onMouseLeave={e => { e.currentTarget.style.background = G50; }}
      >
        View →
      </button>
    ),
  },
];